import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, IconButton, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { ChatMessageDTO, getChatConversation, sendChatMessage } from '../../services/aiChatService';
import VoiceControl from '../gn/VoiceControl';
import { useVoice } from '../../hooks/useVoice';

interface ChatPanelProps {
  role: 'tutor' | 'student';
  courseId: number;
  nodeId: number;
}

/**
 * Persistent, context-aware AI chat rendered as a messaging-app-style side thread (spec 044
 * US1/US3) — separate from the canvas document itself (FR-015). Spec 046 US5 adds voice:
 * a spoken question is transcribed client-side and sent as a normal chat message
 * (inputMode: 'VOICE'), the answer is spoken back, and any options the model offers are
 * shown as tappable/speakable choices (FR-016).
 */
const ChatPanel: React.FC<ChatPanelProps> = ({ role, courseId, nodeId }) => {
  const [messages, setMessages] = useState<ChatMessageDTO[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [streamingText, setStreamingText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [options, setOptions] = useState<string[] | null>(null);
  const streamingTextRef = useRef('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastWasVoiceRef = useRef(false);
  // Breaks the circular dependency: handleSend needs voice.speak, and useVoice needs
  // handleSend as its transcript callback. voice is constructed first with a stable
  // wrapper that reads through this ref, which is (re)assigned after handleSend exists.
  const handleVoiceTranscriptRef = useRef<(text: string) => void>(() => {});

  useEffect(() => {
    getChatConversation(role, courseId, nodeId)
      .then((conv) => setMessages(conv.messages))
      .catch(() => setMessages([]));
  }, [role, courseId, nodeId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, streamingText]);

  const voice = useVoice((transcript) => handleVoiceTranscriptRef.current(transcript));

  const handleSend = useCallback(async (overrideText?: string, viaVoice?: boolean) => {
    const userMessage = (overrideText ?? input).trim();
    if (!userMessage || sending) return;

    setMessages((prev) => [...prev, { role: 'USER', content: userMessage, createdAt: new Date().toISOString() }]);
    setInput('');
    setSending(true);
    setError(null);
    setOptions(null);
    streamingTextRef.current = '';
    setStreamingText('');
    lastWasVoiceRef.current = Boolean(viaVoice);

    await sendChatMessage(role, courseId, nodeId, userMessage, {
      onChunk: (text) => {
        streamingTextRef.current += text;
        setStreamingText(streamingTextRef.current);
      },
      onDone: (data) => {
        setMessages((prev) => [...prev, {
          role: 'ASSISTANT',
          content: streamingTextRef.current,
          createdAt: new Date().toISOString(),
        }]);
        setStreamingText(null);
        setSending(false);
        if (data.options?.length) setOptions(data.options);
        if (lastWasVoiceRef.current) voice.speak(streamingTextRef.current);
      },
      onError: (message) => {
        setError(message);
        setStreamingText(null);
        setSending(false);
      },
    }, viaVoice ? 'VOICE' : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, sending, role, courseId, nodeId]);

  useEffect(() => {
    handleVoiceTranscriptRef.current = (transcript) => handleSend(transcript, true);
  }, [handleSend]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>Ask the AI</Typography>
        <VoiceControl voice={voice} />
      </Box>

      <Box ref={scrollRef} sx={{ flex: 1, overflowY: 'auto', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {messages.length === 0 && !streamingText && (
          <Typography variant="body2" color="text.secondary">
            Ask a question about this topic — I can see what's on the canvas. Tap the mic to ask out loud.
          </Typography>
        )}
        {messages.map((m, i) => (
          <MessageBubble key={i} message={m} />
        ))}
        {streamingText !== null && (
          <MessageBubble message={{ role: 'ASSISTANT', content: streamingText || '…', createdAt: '' }} />
        )}
        {options && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap data-testid="gn-chat-options">
            {options.map((opt) => (
              <Button
                key={opt}
                size="small"
                variant="outlined"
                onClick={() => { setOptions(null); handleSend(opt, lastWasVoiceRef.current); }}
              >
                {opt}
              </Button>
            ))}
          </Stack>
        )}
        {error && <Alert severity="error" sx={{ fontSize: '0.8rem' }}>{error}</Alert>}
      </Box>

      <Box sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 0.5 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Ask about this topic…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          disabled={sending}
          multiline
          maxRows={4}
        />
        <Tooltip title="Send">
          <span>
            <IconButton color="primary" onClick={() => handleSend()} disabled={sending || !input.trim()}>
              {sending ? <CircularProgress size={20} /> : <SendIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};

const MessageBubble: React.FC<{ message: ChatMessageDTO }> = ({ message }) => {
  const isUser = message.role === 'USER';
  return (
    <Paper
      elevation={0}
      sx={{
        alignSelf: isUser ? 'flex-end' : 'flex-start',
        maxWidth: '85%',
        p: 1,
        px: 1.5,
        bgcolor: isUser ? 'primary.main' : 'action.hover',
        color: isUser ? 'primary.contrastText' : 'text.primary',
        borderRadius: 2,
      }}
    >
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{message.content}</Typography>
    </Paper>
  );
};

export default ChatPanel;
